/* eslint-disable @typescript-eslint/member-ordering */
import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { WorkflowStepContent } from '../../../shared/components/workflow-steps/workflow-steps.component';
// eslint-disable-next-line @stylistic/max-len
import { CallableOrderItem, CalledVolume, EcallOffByVolumeStepState, EcallResponse, GroupCalledVolume } from '../../models/ecall-off-model';
import { ECallOffService } from '../../services/ecall-off.service';
import { UNDERSCORE } from '../../../shared/utils/constant';
import { CallableOrderItemChange } from '../card/callable-order-item.component';
import { DATE_FORMAT_PATTERN } from '../../../shared/models/filter-formats';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { EcallOffHeaderComponent } from '../header/ecall-off-header.component';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { SnackbarSaveEcallOffComponent } from '../utils/snackbar-save-ecall-off/snackbar-save-ecall-off.component';

const DATE_FORMAT = 'YYYY-MM-DD';

@Component({
	selector: 'copo-by-volume-step-summary',
	templateUrl: './by-volume-step-summary.component.html',
	styleUrl: './by-volume-step-summary.component.css',
	animations: pageTransitions,
	imports: [
		EcallOffHeaderComponent,
		TranslateModule,
		NgFor,
		NgIf,
		MatIconModule,
		MatTooltipModule,
		NumberPipe,
		DatePipe
	]
})

export class ByVolumeStepSummaryComponent implements WorkflowStepContent {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() groupAllVolume?: GroupCalledVolume[] = [];
	@Input() editDateFrom = false;
	@Input() editDateTo = false;
	@Output() readonly callableOrderItemChange = new EventEmitter<CallableOrderItemChange>(true);
	@Output() readonly callableOrderItemDelete = new EventEmitter<CallableOrderItem>(true);
	@Output() readonly callableOrderItemView = new EventEmitter<CallableOrderItem>(true);

	readonly UNDERSCORE = UNDERSCORE;
	readonly CURRENT_DATE = moment().format(DATE_FORMAT_PATTERN);

	isShipTo = false;
	calledVolumes: CalledVolume[] = [];
	showComments = false;
	showDate = false;

	getUpdatedStepState() {
		return this.stepState;
	}

	stepSummary = false;

	stepState?: EcallOffByVolumeStepState;
	canShowComments = false;
	groupCalledVolume: GroupCalledVolume[] = [];
	constructor(
		private readonly ecallOffService: ECallOffService,
		private readonly snackBar: MatSnackBar,
	) {}

	isStepValidated() {
		return true;
	}

	setStepState(state: Record<string, unknown> | undefined) {
		if (state) {
			this.stepState = <EcallOffByVolumeStepState | undefined>state;
			this.canShowComments = this.stepState?.selectedOrderItems?.some(
				orderItem => orderItem.comment
			) ?? false;
			this.isShipTo = this.stepState?.selectedOrderItems?.some(
				orderItem => orderItem.shipTo?.shipToDescription
			) ?? false;
			this.showDate = this.stepState?.selectedOrderItems?.some(
				orderItem => orderItem.requestedDeliveryDateFrom || orderItem.requestedDeliveryDateTo
			) ?? false;
			this.stepSummary = true;
			this.getEcallDetails();
		}
	}

	canGoBack() {
		return this.stepState?.selectedOrderItems === undefined || this.stepState.selectedOrderItems.length === 0;
	}

	async submit() {
		if (!this.stepState?.customerCalloffReference
			|| !this.stepState.selectedOrderItems) {
			return;
		}
		const body: EcallResponse = {
			type: 'P',
			customerReference: this.stepState.customerCalloffReference,
			copyTo: this.stepState.copyTo,
			rddMode: this.stepState.rddMode,
			ecallItems: this.stepState.selectedOrderItems.map((orderItem, index) => {
				if (!orderItem.supplierOrderItemNumber
					|| !orderItem.supplierOrderNumber
					|| !orderItem.mill
					|| !orderItem.calledWeight
				) {
					const error = 'Invalid data when mapping ecallItems to save';
					console.error(`${ error } ,index [${ index }]`, orderItem);
					throw new Error(error);
				}
				const dateFrom = orderItem.requestedDeliveryDateFrom
					? moment(orderItem.requestedDeliveryDateFrom).format(DATE_FORMAT) : undefined;
				const dateTo = orderItem.requestedDeliveryDateTo
					? moment(orderItem.requestedDeliveryDateTo).format(DATE_FORMAT) : undefined;
				return {
					comment: orderItem.comment,
					millCode: orderItem.mill,
					millDescription: orderItem.millDescription,
					requestedDeliveryDateFrom: dateFrom,
					requestedDeliveryDateTo: dateTo,
					requestedVolume: orderItem.calledWeight,
					requestedVolumeUnit: 'To',
					supplierOrderItem: orderItem.supplierOrderItemNumber,
					supplierOrderReference: orderItem.supplierOrderNumber,
					unloadingReference: orderItem.unloadingReference,
					shipTo: typeof orderItem.shipTo === 'object' ? orderItem.shipTo.shipTo : orderItem.shipTo,
					shipToDescription: typeof orderItem.shipTo === 'object'
						? orderItem.shipTo.shipToDescription : orderItem.shipToDescription,
				};
			})
		};
		const id = await firstValueFrom(this.ecallOffService.saveEcall(body));
		body.id = id;
		this.snackBar.openFromComponent(
			SnackbarSaveEcallOffComponent,
			{
				duration: 5000,
				panelClass: ['copo-claim-draft-snackbar'],
				data: body.customerReference
			}
		);
	}

	getTotalWeight() {
		if (!this.stepState?.selectedOrderItems) {
			return 0;
		}
		let weight = 0;
		for (const item of this.stepState.selectedOrderItems) {
			weight += item.calledWeight ?? 0;
		}
		return weight;
	}

	getEcallDetails() {
		if (this.stepState?.selectedOrderItems) {
			for (const elementVolume of this.stepState.selectedOrderItems) {
				const currentShipToObj = typeof elementVolume.shipTo === 'object' ? elementVolume.shipTo : {
					shipTo: elementVolume.shipTo,
					shipToDescription: elementVolume.shipToDescription
				};
				const originalShipToObj = typeof elementVolume.originalShipTo === 'object'
					? elementVolume.originalShipTo : {
						shipTo: elementVolume.originalShipTo,
						shipToDescription: elementVolume.shipToDescription
					};
				const calledVolume: CalledVolume = {
					millCode: elementVolume.mill ?? '',
					requestedVolume: elementVolume.calledWeight ?? 0,
					supplierOrderItem: elementVolume.supplierOrderItemNumber ?? '',
					supplierOrderReference: elementVolume.supplierOrderNumber ?? '',
					millDescription: elementVolume.millDescription,
					unloadingReference: elementVolume.unloadingReference,
					customerOrderItem: elementVolume.customerOrderItemNumber,
					customerOrderReference: elementVolume.customerOrderNumber,
					comment: elementVolume.comment,
					requestedDeliveryDateFrom: elementVolume.requestedDeliveryDateFrom?.toDateString(),
					requestedDeliveryDateTo: elementVolume.requestedDeliveryDateTo?.toDateString(),
					shipTo: currentShipToObj.shipTo,
					shipToDescription: currentShipToObj.shipToDescription,
					originalShipTo: originalShipToObj.shipTo,
					originalShipToDescription: originalShipToObj.shipToDescription,
					requestedVolumeUnit: ''
				};
				this.calledVolumes.push(calledVolume);
			}
			if (this.calledVolumes.length > 0) {
				const groupedKeysVolume = this.calledVolumes
					.reduce((group: Record<string, CalledVolume[]>, item: CalledVolume) => {
						// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
						if (!Object.hasOwn(group, item.millCode ?? '')) {
							// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
							group[item.millCode ?? ''] = [];
						}
						// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
						group[item.millCode ?? ''].push(item);
						return group;
					}, {});
				const groupKeysVolume = Object.keys(groupedKeysVolume);
				for (const elementVolume of groupKeysVolume) {
					this.groupCalledVolume.push({
						millCode: elementVolume,
						millCodeGroup: groupedKeysVolume[elementVolume],
						requestedVolume: this.getTotalGrossWeight(groupedKeysVolume[elementVolume])
					});
				}
			}
		}
	}

	getTotalGrossWeight(volumes: CalledVolume[]) {
		return volumes.reduce((sum, batchWeight) => sum + Number(batchWeight.requestedVolume || 0), 0);
	}

	getCustomerOrderNumber(customerOrderItem: string | undefined, customerOrderReference: string | undefined) {
		const baseValue = customerOrderReference;
		if (baseValue) {
			if (customerOrderItem) {
				return `${ baseValue }/${ customerOrderItem }`;
			}
			return baseValue;
		}
		return ' ';
	}

	getSupplierOrderNumber(supplierOrderItem: string | undefined, supplierOrderReference: string | undefined) {
		const baseValue = supplierOrderReference;
		if (baseValue) {
			if (supplierOrderItem) {
				return `${ baseValue }/${ supplierOrderItem }`;
			}
			return baseValue;
		}
		return '';
	}
}
